import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useData } from '../DataContext';
import { useLang } from '../LanguageContext'; // ★ 언어 훅
import { 
  Landmark, User, Shield, Receipt, Briefcase, Printer, X, CheckCircle, ArrowRightLeft, Send, Bluetooth 
} from 'lucide-react';

const Cage = () => {
  // DataContext에서 필요한 모든 데이터와 함수 가져오기
  const { 
    agents = [], 
    processAgentTransaction, 
    addCageLog, 
    addHandoverLog, 
    sendSms, 
    agentTransactions = [], 
    transactions = {}, 
    cageLogs = [], 
    handoverLogs = [], 
    telegramLogs = [], 
    smsTemplates = [], 
    addSmsTemplate, 
    deleteSmsTemplate, 
    funds, 
    globalCurrency, 
    sysConfig 
  } = useData();

  const { t } = useLang(); // ★ 번역 함수 사용
  
  // --- UI State ---
  const [activeTab, setActiveTab] = useState('Agent'); 
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // --- Helper Functions ---
  const fmt = (n, decimals = 0) => {
      if (!n) return '0';
      const num = parseFloat(String(n).replace(/,/g, ''));
      return isNaN(num) ? '0' : num.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  };
  const parseNum = (s) => parseFloat(String(s).replace(/,/g, '')) || 0;

  // --- 실시간 시스템 잔고 계산 (Handover 참조용) ---
  const systemState = useMemo(() => {
      let currentCash = parseNum(funds?.cash || 0);
      let currentCashChips = parseNum(funds?.cashChips || 0);
      let currentRollingChips = parseNum(funds?.rollingChips || 0);

      // Agent Transactions 반영
      agentTransactions.forEach(tx => {
          const amt = parseNum(tx.amount);
          if (['Deposit', 'Expense Transfer'].includes(tx.type)) {
              currentCash += amt;
          } else if (['Withdraw'].includes(tx.type)) {
              currentCash -= amt;
          }
      });

      // Game Transactions 반영
      Object.values(transactions).flat().forEach(tx => {
          const amt = parseNum(tx.amount);
          const rReturn = parseNum(tx.rollingChips);
          const cReturn = parseNum(tx.cashChips);

          if (['Initial Buy-in', 'Add Buy-in'].includes(tx.type)) {
              currentRollingChips -= amt;
          }
          if (['Chips Out', 'Cash-out', 'Settle', 'Return'].includes(tx.type)) {
              currentRollingChips += rReturn;
              currentCashChips += cReturn;
          }
      });

      // Cage Logs (경비 및 외부 입출금) 반영
      cageLogs.forEach(log => {
          const amt = parseNum(log.amount);
          if (log.type === 'Expense') {
              currentCash -= amt;
          } else if (log.type === 'External') {
              if (log.itemType === 'Cash') {
                  if (log.category === 'In') currentCash += amt;
                  else if (log.category === 'Out') currentCash -= amt;
              } else if (log.itemType === 'Cash Chips') {
                  if (log.category === 'In') currentCashChips += amt;
                  else if (log.category === 'Out') currentCashChips -= amt;
              } else if (log.itemType === 'Rolling Chips') {
                  if (log.category === 'In') currentRollingChips += amt;
                  else if (log.category === 'Out') currentRollingChips -= amt;
              }
          }
      });

      return { cash: currentCash, cashChips: currentCashChips, rollingChips: currentRollingChips };
  }, [funds, agentTransactions, transactions, cageLogs]);

  // --- Dashboard Stats ---
  const [stats, setStats] = useState({ todayIn: 0, todayOut: 0, balance: 0 });

  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];

    const agentIn = agentTransactions.filter(t => (t.type === 'Deposit' || t.type === 'Expense Transfer') && t.date.startsWith(today)).reduce((s,t)=>s+(parseFloat(t.amount)||0),0);
    const agentOut = agentTransactions.filter(t => t.type === 'Withdraw' && t.date.startsWith(today)).reduce((s,t)=>s+(parseFloat(t.amount)||0),0);

    const expenseOut = cageLogs.filter(t => t.type === 'Expense' && t.date.startsWith(today)).reduce((s,t)=>s+(parseFloat(t.amount)||0),0);

    const extIn = cageLogs.filter(t => t.type === 'External' && t.category === 'In' && t.itemType === 'Cash' && t.date.startsWith(today)).reduce((s,t)=>s+(parseFloat(t.amount)||0),0);
    const extOut = cageLogs.filter(t => t.type === 'External' && t.category === 'Out' && t.itemType === 'Cash' && t.date.startsWith(today)).reduce((s,t)=>s+(parseFloat(t.amount)||0),0);

    setStats({
        todayIn: agentIn + extIn,
        todayOut: agentOut + expenseOut + extOut,
        balance: systemState.cash 
    });
  }, [agentTransactions, cageLogs, systemState]);

  // --- Input States (모든 탭의 입력 상태) ---
  // Agent Tab
  const [agentAmount, setAgentAmount] = useState('');
  const [agentRemark, setAgentRemark] = useState('');
  const [agentTxType, setAgentTxType] = useState('Deposit'); 

  // Safety Tab
  const [safetyName, setSafetyName] = useState('');
  const [safetyItem, setSafetyItem] = useState('Cash'); 
  const [safetyCurrency, setSafetyCurrency] = useState(globalCurrency); 
  const [safetyAmount, setSafetyAmount] = useState('');
  const [safetyRemark, setSafetyRemark] = useState('');
  const [safetyType, setSafetyType] = useState('In'); 
  const [lastSafetyLog, setLastSafetyLog] = useState(null); 
  const [showSuccessModal, setShowSuccessModal] = useState(false); 

  // Expense Tab
  const [expenseCategory, setExpenseCategory] = useState('Hotel Room');
  const [expenseAmount, setExpenseAmount] = useState('');
  const [expenseRemark, setExpenseRemark] = useState('');
  const [isTransferFromAgent, setIsTransferFromAgent] = useState(false); 

  // External Tab
  const [extTarget, setExtTarget] = useState('Bank'); 
  const [extItem, setExtItem] = useState('Cash'); 
  const [extType, setExtType] = useState('Out'); 
  const [extAmount, setExtAmount] = useState('');
  const [extRemark, setExtRemark] = useState('');

  // Handover Tab
  const [handoverData, setHandoverData] = useState({ cashChips: '', rollingChips: '', cash: '', incomingStaff: '', outgoingStaff: '' });
  const [handoverSigs, setHandoverSigs] = useState({ outgoing: null, incoming: null });

  // Telegram Tab
  const [msgRecipients, setMsgRecipients] = useState([]); 
  const [msgContent, setMsgContent] = useState('');

  // Signature & Bluetooth
  const [isSigModalOpen, setIsSigModalOpen] = useState(false);
  const [sigTarget, setSigTarget] = useState(''); 
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);

  // --- Handlers ---
  const getAgentDisplayName = (agent) => {
      if (!agent) return '';
      return `${agent.code} - ${agent.engSurname} ${agent.engGivenName} ${agent.nativeName ? `(${agent.nativeName})` : ''}`;
  };

  const findAgentByCode = (code) => {
      return agents.find(a => a.code === code);
  };

  // Agent Transaction Handler
  const handleAgentTx = async () => {
    if (!selectedAgent) return alert("Select Agent.");
    const amt = parseNum(agentAmount);
    if (amt <= 0) return alert("Enter amount.");
    const success = await processAgentTransaction(selectedAgent.docId, selectedAgent.code, agentTxType, amt, agentRemark);
    if (success) { alert("Success."); setAgentAmount(''); setAgentRemark(''); } 
    else { alert("Error (Insufficient funds)"); }
  };

  // Safety Deposit: 서명 모달 열기
  const handleSafetyTx = () => {
    if (!safetyName) return alert("Enter name.");
    const amt = parseNum(safetyAmount);
    if (amt <= 0) return alert("Enter amount.");
    
    setSigTarget('safety');
    setIsSigModalOpen(true);
  };

  // Safety Deposit: 서명 완료 후 실제 저장
  const executeSafetyTx = async (signatureData) => {
    const amt = parseNum(safetyAmount);
    const logData = { depositor: safetyName, itemType: safetyItem, signature: signatureData, currency: safetyCurrency }; 
    
    await addCageLog('Safety Deposit', safetyType, amt, safetyRemark, logData);
    
    setLastSafetyLog({ 
        ...logData, 
        type: 'Safety Deposit', 
        category: safetyType, 
        amount: amt, 
        remark: safetyRemark, 
        date: new Date().toISOString(), 
        id: 'SD-' + Date.now().toString().slice(-6) 
    });
    
    setShowSuccessModal(true);
    setSafetyName(''); setSafetyAmount(''); setSafetyRemark('');
  };

  // Expense Handler
  const handleExpenseTx = async () => {
    const amt = parseNum(expenseAmount);
    if (amt <= 0) return alert("Enter amount.");
    let remark = expenseRemark;
    if (isTransferFromAgent) {
        if (!selectedAgent) return alert("Select agent.");
        const success = await processAgentTransaction(selectedAgent.docId, selectedAgent.code, 'Expense Transfer', amt, `Expense Transfer to Cage (${expenseCategory})`);
        if (!success) return alert("Insufficient agent balance.");
        remark += ` (Paid by ${selectedAgent.code})`;
    }
    await addCageLog('Expense', expenseCategory, amt, remark);
    alert("Expense recorded.");
    setExpenseAmount(''); setExpenseRemark(''); setIsTransferFromAgent(false);
  };

  // External Transaction Handler
  const handleExternalTx = async () => {
      const amt = parseNum(extAmount);
      if (amt <= 0) return alert("Enter amount.");
      await addCageLog('External', extType, amt, extRemark, { target: extTarget, itemType: extItem });
      alert("External tx recorded.");
      setExtAmount(''); setExtRemark('');
  };

  // Handover Submit Handler
  const handleHandoverSubmit = async () => {
      if(!handoverData.incomingStaff || !handoverData.outgoingStaff) return alert("Enter Staff IDs.");
      if(!handoverSigs.outgoing || !handoverSigs.incoming) return alert("Signatures required.");
      
      await addHandoverLog({ ...handoverData, signatures: handoverSigs });
      alert("Handover saved.");
      setHandoverData({ cashChips: '', rollingChips: '', cash: '', incomingStaff: '', outgoingStaff: '' });
      setHandoverSigs({ outgoing: null, incoming: null });
  };

  // Telegram Handlers
  const handleAddRecipient = (agent) => {
      if (!msgRecipients.find(a => a.id === agent.id)) {
          setMsgRecipients([...msgRecipients, agent]);
      }
      setSearchTerm(''); 
  };
  const handleRemoveRecipient = (id) => {
      setMsgRecipients(msgRecipients.filter(a => a.id !== id));
  };
  const handleSelectAllAgents = () => {
      setMsgRecipients(agents);
  };
  const handleTelegramSend = async () => {
      if (msgRecipients.length === 0) return alert("Select recipients.");
      if (!msgContent) return alert("Enter message.");
      for (const agent of msgRecipients) {
          await sendSms(agent.code, 'MANUAL_MSG', msgContent); 
      }
      alert(`Sent to ${msgRecipients.length} agents.`);
      setMsgContent('');
      setMsgRecipients([]);
  };
  const handleSaveTemplate = () => {
      if (!msgContent) return alert("Enter content.");
      const title = prompt("Template Title:");
      if (title) {
          addSmsTemplate(title, msgContent);
          alert("Template saved.");
      }
  };

  // Signature & Bluetooth Logic
  const openSigModal = (target) => { setSigTarget(target); setIsSigModalOpen(true); setTimeout(() => { const canvas = canvasRef.current; if(canvas) { const ctx = canvas.getContext('2d'); ctx.clearRect(0, 0, canvas.width, canvas.height); } }, 100); };
  
  const startDrawing = (e) => { const canvas = canvasRef.current; const ctx = canvas.getContext('2d'); const rect = canvas.getBoundingClientRect(); const x = (e.clientX || e.touches[0].clientX) - rect.left; const y = (e.clientY || e.touches[0].clientY) - rect.top; ctx.beginPath(); ctx.moveTo(x, y); setIsDrawing(true); };
  const draw = (e) => { if (!isDrawing) return; const canvas = canvasRef.current; const ctx = canvas.getContext('2d'); const rect = canvas.getBoundingClientRect(); const x = (e.clientX || e.touches[0].clientX) - rect.left; const y = (e.clientY || e.touches[0].clientY) - rect.top; ctx.lineTo(x, y); ctx.stroke(); };
  
  const saveSignature = () => { 
      const dataUrl = canvasRef.current.toDataURL(); 
      if (sigTarget === 'outgoing') {
          setHandoverSigs(prev => ({ ...prev, outgoing: dataUrl }));
      } else if (sigTarget === 'incoming') {
          setHandoverSigs(prev => ({ ...prev, incoming: dataUrl }));
      } else if (sigTarget === 'safety') {
          executeSafetyTx(dataUrl); 
      }
      setIsSigModalOpen(false); 
  };
  
  const connectBluetoothPad = async () => {
      if (!navigator.bluetooth) return alert("Bluetooth not supported in this browser.");
      try {
          const device = await navigator.bluetooth.requestDevice({ acceptAllDevices: true });
          alert(`${device.name} connected.`);
      } catch (error) {
          console.error(error);
          alert("Connection failed.");
      }
  };

  const filteredAgents = agents.filter(a => {
    const code = (a.code || '').toUpperCase();
    const name = ((a.engSurname || '') + ' ' + (a.engGivenName || '') + ' ' + (a.nativeName || '')).toUpperCase();
    const search = searchTerm.toUpperCase();
    return code.includes(search) || name.includes(search);
  });

  return (
    <div style={{ padding: '30px' }}>
      
      {/* 1. TOP DASHBOARD */}
      <div style={{ display:'flex', gap:'20px', marginBottom:'30px' }}>
          <div style={{ flex:1, background:'white', padding:'20px', borderRadius:'12px', boxShadow:'0 4px 6px rgba(0,0,0,0.05)', borderLeft:'5px solid #2563eb', textAlign:'right' }}><div style={{ color:'#64748b', fontSize:'14px', fontWeight:'bold', marginBottom:'5px' }}>{t('cage_balance')} ({globalCurrency})</div><div style={{ fontSize:'28px', fontWeight:'900', color:'#1e293b' }}>{fmt(stats.balance)}</div></div>
          <div style={{ flex:1, background:'white', padding:'20px', borderRadius:'12px', boxShadow:'0 4px 6px rgba(0,0,0,0.05)', borderLeft:'5px solid #16a34a', textAlign:'right' }}><div style={{ color:'#64748b', fontSize:'14px', fontWeight:'bold', marginBottom:'5px' }}>{t('today_in')}</div><div style={{ fontSize:'28px', fontWeight:'900', color:'#16a34a' }}>+ {fmt(stats.todayIn)}</div></div>
          <div style={{ flex:1, background:'white', padding:'20px', borderRadius:'12px', boxShadow:'0 4px 6px rgba(0,0,0,0.05)', borderLeft:'5px solid #dc2626', textAlign:'right' }}><div style={{ color:'#64748b', fontSize:'14px', fontWeight:'bold', marginBottom:'5px' }}>{t('today_out')}</div><div style={{ fontSize:'28px', fontWeight:'900', color:'#dc2626' }}>- {fmt(stats.todayOut)}</div></div>
      </div>

      <h2 style={{ display:'flex', alignItems:'center', gap:'10px', color:'#1e293b' }}>
        <Landmark size={28} /> {t('menu_cage')}
      </h2>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', flexWrap:'wrap' }}>
        {['Agent', 'Expense', 'Safety', 'External', 'Handover', 'Telegram'].map(tab => (
          <button 
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              padding: '12px 24px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontWeight: 'bold', fontSize: '15px',
              backgroundColor: activeTab === tab ? '#0f172a' : '#e2e8f0',
              color: activeTab === tab ? 'white' : '#64748b',
              display: 'flex', alignItems: 'center', gap: '8px'
            }}
          >
            {tab === 'Agent' && <User size={18}/>}
            {tab === 'Expense' && <Receipt size={18}/>}
            {tab === 'Safety' && <Shield size={18}/>}
            {tab === 'External' && <ArrowRightLeft size={18}/>}
            {tab === 'Handover' && <Briefcase size={18}/>}
            {tab === 'Telegram' && <Send size={18}/>}
            
            {/* ★ [다국어] 탭 이름 */}
            {tab === 'Agent' ? t('tab_agent_io') : 
             tab === 'Expense' ? t('tab_expenses') : 
             tab === 'Safety' ? t('tab_safety') : 
             tab === 'External' ? t('tab_vault') : 
             tab === 'Telegram' ? t('tab_telegram') :
             t('tab_handover')}
          </button>
        ))}
      </div>

      <div style={{ display: 'flex', gap: '20px' }}>
        {/* Left Panel */}
        <div style={{ flex: 1, backgroundColor: 'white', padding: '30px', borderRadius: '12px', boxShadow: '0 4px 15px rgba(0,0,0,0.05)' }}>
          
          {/* --- Tab Content: Agent --- */}
          {activeTab === 'Agent' && (
            <div>
              <h3 style={{ marginTop:0, marginBottom:'20px', color:'#334155' }}>{t('tab_agent_io')}</h3>
              <div style={{ marginBottom: '15px' }}><label style={{ display:'block', fontSize:'13px', fontWeight:'bold', color:'#64748b', marginBottom:'5px' }}>{t('label_agent_search')}</label><div style={{ position:'relative' }}><input placeholder={t('search')} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} style={{ width:'100%', padding:'12px', borderRadius:'6px', border:'1px solid #cbd5e1' }} />{searchTerm && (<div style={{ position:'absolute', top:'100%', left:0, width:'100%', maxHeight:'200px', overflowY:'auto', background:'white', border:'1px solid #cbd5e1', zIndex:10 }}>{filteredAgents.map(a => (<div key={a.id} onClick={() => { setSelectedAgent(a); setSearchTerm(a.code); }} style={{ padding:'10px', cursor:'pointer', borderBottom:'1px solid #f1f5f9' }}><span style={{ fontWeight:'bold', color:'#2563eb' }}>{getAgentDisplayName(a)}</span></div>))}</div>)}</div></div>
              {selectedAgent && (<div style={{ padding:'15px', background:'#eff6ff', borderRadius:'8px', marginBottom:'20px', border:'1px solid #bfdbfe' }}><div style={{ fontSize:'14px', color:'#1e40af' }}>Selected: <strong>{getAgentDisplayName(selectedAgent)}</strong></div><div style={{ fontSize:'20px', fontWeight:'bold', color:'#1e3a8a', marginTop:'5px' }}>Balance: {fmt(selectedAgent.balance)}</div></div>)}
              {/* ★ [다국어] 입/출금 버튼 */}
              <div style={{ display:'flex', gap:'10px', marginBottom:'15px' }}><button onClick={() => setAgentTxType('Deposit')} style={{ flex:1, padding:'10px', borderRadius:'6px', border:'1px solid #cbd5e1', background: agentTxType==='Deposit'?'#dcfce7':'white', color: agentTxType==='Deposit'?'#166534':'#64748b', fontWeight:'bold', cursor:'pointer' }}>{t('btn_deposit')}</button><button onClick={() => setAgentTxType('Withdraw')} style={{ flex:1, padding:'10px', borderRadius:'6px', border:'1px solid #cbd5e1', background: agentTxType==='Withdraw'?'#fee2e2':'white', color: agentTxType==='Withdraw'?'#991b1b':'#64748b', fontWeight:'bold', cursor:'pointer' }}>{t('btn_withdraw')}</button></div>
              <div style={{ marginBottom: '15px' }}><label style={{ display:'block', fontSize:'13px', fontWeight:'bold', color:'#64748b', marginBottom:'5px' }}>{t('label_amount')}</label><input value={agentAmount} onChange={e => setAgentAmount(Number(e.target.value.replace(/[^0-9]/g,'')).toLocaleString())} style={{ width:'100%', padding:'12px', borderRadius:'6px', border:'1px solid #cbd5e1', fontSize:'16px', fontWeight:'bold' }} placeholder="0" /></div>
              <div style={{ marginBottom: '20px' }}><label style={{ display:'block', fontSize:'13px', fontWeight:'bold', color:'#64748b', marginBottom:'5px' }}>{t('label_remark')}</label><input value={agentRemark} onChange={e => setAgentRemark(e.target.value)} style={{ width:'100%', padding:'12px', borderRadius:'6px', border:'1px solid #cbd5e1' }} placeholder="..." /></div>
              {/* ★ [다국어] 제출 버튼 */}
              <button onClick={handleAgentTx} style={{ width:'100%', padding:'15px', background:'#0f172a', color:'white', border:'none', borderRadius:'8px', fontWeight:'bold', cursor:'pointer' }}>{t('btn_submit')}</button>
            </div>
          )}
          
          {/* --- Tab Content: Expense --- */}
          {activeTab === 'Expense' && (
            <div>
              <h3 style={{ marginTop:0, marginBottom:'20px', color:'#334155' }}>{t('tab_expenses')}</h3>
              <div style={{ marginBottom: '15px' }}>
                  <label style={{ display:'block', fontSize:'13px', fontWeight:'bold', color:'#64748b', marginBottom:'5px' }}>Category</label>
                  <select value={expenseCategory} onChange={e => setExpenseCategory(e.target.value)} style={{ width:'100%', padding:'12px', borderRadius:'6px', border:'1px solid #cbd5e1' }}>
                      <option value="Hotel Room">Hotel Room</option>
                      <option value="F&B">F&B</option>
                      <option value="Air Ticket">Air Ticket</option>
                      <option value="Transportation">Transportation</option>
                      <option value="Operation Fund">Operation Fund</option>
                      <option value="Marketing Fund">Marketing Fund</option>
                      <option value="Misc">Misc.</option>
                  </select>
              </div>
              <div style={{ marginBottom: '15px' }}><label style={{ display:'block', fontSize:'13px', fontWeight:'bold', color:'#64748b', marginBottom:'5px' }}>{t('label_amount')}</label><input value={expenseAmount} onChange={e => setExpenseAmount(Number(e.target.value.replace(/[^0-9]/g,'')).toLocaleString())} style={{ width:'100%', padding:'12px', borderRadius:'6px', border:'1px solid #cbd5e1', fontSize:'16px', fontWeight:'bold' }} placeholder="0" /></div>
              <div style={{ marginBottom: '15px', padding:'15px', background:'#f8fafc', borderRadius:'8px', border:'1px solid #e2e8f0' }}><label style={{ display:'flex', alignItems:'center', gap:'8px', cursor:'pointer' }}><input type="checkbox" checked={isTransferFromAgent} onChange={e => setIsTransferFromAgent(e.target.checked)} /><span style={{ fontWeight:'bold', fontSize:'14px' }}>Transfer from Agent Account</span></label>{isTransferFromAgent && (<div style={{ marginTop:'10px' }}><input placeholder="Search Agent..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} style={{ width:'100%', padding:'10px', borderRadius:'6px', border:'1px solid #cbd5e1', fontSize:'13px' }} />{searchTerm && (<div style={{ maxHeight:'150px', overflowY:'auto', border:'1px solid #e2e8f0', marginTop:'5px', background:'white' }}>{filteredAgents.map(a => (<div key={a.id} onClick={() => { setSelectedAgent(a); setSearchTerm(a.code); }} style={{ padding:'8px', cursor:'pointer', borderBottom:'1px solid #f1f5f9', fontSize:'12px' }}><span style={{ fontWeight:'bold', color:'#2563eb' }}>{getAgentDisplayName(a)}</span> (Bal: {fmt(a.balance)})</div>))}</div>)}</div>)}</div>
              <div style={{ marginBottom: '20px' }}><label style={{ display:'block', fontSize:'13px', fontWeight:'bold', color:'#64748b', marginBottom:'5px' }}>{t('label_remark')}</label><input value={expenseRemark} onChange={e => setExpenseRemark(e.target.value)} style={{ width:'100%', padding:'12px', borderRadius:'6px', border:'1px solid #cbd5e1' }} placeholder="..." /></div>
              <button onClick={handleExpenseTx} style={{ width:'100%', padding:'15px', background:'#dc2626', color:'white', border:'none', borderRadius:'8px', fontWeight:'bold', cursor:'pointer' }}>{t('save')}</button>
            </div>
          )}
          
          {/* --- Tab Content: Safety --- */}
          {activeTab === 'Safety' && (
            <div>
              <h3 style={{ marginTop:0, marginBottom:'20px', color:'#334155' }}>{t('tab_safety')}</h3>
              <div style={{ display:'flex', gap:'10px', marginBottom:'15px' }}><button onClick={() => setSafetyType('In')} style={{ flex:1, padding:'10px', borderRadius:'6px', border:'1px solid #cbd5e1', background: safetyType==='In'?'#dbeafe':'white', color: safetyType==='In'?'#1e40af':'#64748b', fontWeight:'bold' }}>In</button><button onClick={() => setSafetyType('Out')} style={{ flex:1, padding:'10px', borderRadius:'6px', border:'1px solid #cbd5e1', background: safetyType==='Out'?'#f3e8ff':'white', color: safetyType==='Out'?'#6b21a8':'#64748b', fontWeight:'bold' }}>Out</button></div>
              <div style={{ marginBottom: '15px' }}><label style={{ display:'block', fontSize:'13px', fontWeight:'bold', color:'#64748b', marginBottom:'5px' }}>Customer Name / Passport No.</label><input value={safetyName} onChange={e => setSafetyName(e.target.value)} style={{ width:'100%', padding:'12px', borderRadius:'6px', border:'1px solid #cbd5e1' }} placeholder="..." /></div>
              <div style={{ marginBottom: '15px' }}>
                  <label style={{ display:'block', fontSize:'13px', fontWeight:'bold', color:'#64748b', marginBottom:'5px' }}>Item Type</label>
                  <select value={safetyItem} onChange={e => setSafetyItem(e.target.value)} style={{ width:'100%', padding:'12px', borderRadius:'6px', border:'1px solid #cbd5e1' }}><option value="Cash">Cash</option><option value="Chips">Chips</option><option value="Valuables">Valuables</option></select>
              </div>
              <div style={{ marginBottom: '15px', display:'flex', gap:'10px' }}>
                  <div style={{ width:'120px' }}>
                      <label style={{ display:'block', fontSize:'13px', fontWeight:'bold', color:'#64748b', marginBottom:'5px' }}>Currency</label>
                      <select value={safetyCurrency} onChange={e => setSafetyCurrency(e.target.value)} style={{ width:'100%', padding:'12px', borderRadius:'6px', border:'1px solid #cbd5e1', background:'#f8fafc', fontWeight:'bold' }}>
                          <option value={globalCurrency}>{globalCurrency}</option>
                          {['PHP', 'USD', 'HKD', 'KRW', 'CNY', 'JPY'].filter(c => c !== globalCurrency).map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                  </div>
                  <div style={{ flex:1 }}>
                      <label style={{ display:'block', fontSize:'13px', fontWeight:'bold', color:'#64748b', marginBottom:'5px' }}>{t('label_amount')}</label>
                      <input value={safetyAmount} onChange={e => setSafetyAmount(Number(e.target.value.replace(/[^0-9]/g,'')).toLocaleString())} style={{ width:'100%', padding:'12px', borderRadius:'6px', border:'1px solid #cbd5e1', fontSize:'16px', fontWeight:'bold' }} placeholder="0" />
                  </div>
              </div>
              <div style={{ marginBottom: '20px' }}><label style={{ display:'block', fontSize:'13px', fontWeight:'bold', color:'#64748b', marginBottom:'5px' }}>{t('label_remark')}</label><input value={safetyRemark} onChange={e => setSafetyRemark(e.target.value)} style={{ width:'100%', padding:'12px', borderRadius:'6px', border:'1px solid #cbd5e1' }} placeholder="..." /></div>
              <button onClick={handleSafetyTx} style={{ width:'100%', padding:'15px', background:'#2563eb', color:'white', border:'none', borderRadius:'8px', fontWeight:'bold', cursor:'pointer' }}>{t('btn_submit')}</button>
            </div>
          )}

          {/* --- Tab Content: External --- */}
          {activeTab === 'External' && (
            <div>
              <h3 style={{ marginTop:0, marginBottom:'20px', color:'#334155' }}>{t('tab_vault')}</h3>
              <div style={{ marginBottom: '15px' }}><label style={{ display:'block', fontSize:'13px', fontWeight:'bold', color:'#64748b', marginBottom:'5px' }}>Target</label><select value={extTarget} onChange={e => setExtTarget(e.target.value)} style={{ width:'100%', padding:'12px', borderRadius:'6px', border:'1px solid #cbd5e1' }}><option>Bank</option><option>Main Casino</option><option>Other</option></select></div>
              <div style={{ display:'flex', gap:'10px', marginBottom:'15px' }}><div style={{flex:1}}><label style={{ display:'block', fontSize:'13px', fontWeight:'bold', color:'#64748b', marginBottom:'5px' }}>Item Type</label><select value={extItem} onChange={e => setExtItem(e.target.value)} style={{ width:'100%', padding:'12px', borderRadius:'6px', border:'1px solid #cbd5e1' }}><option>Cash</option><option>Cash Chips</option><option>Rolling Chips</option></select></div><div style={{flex:1}}><label style={{ display:'block', fontSize:'13px', fontWeight:'bold', color:'#64748b', marginBottom:'5px' }}>Type</label><div style={{display:'flex', gap:'5px'}}><button onClick={()=>setExtType('In')} style={{flex:1, padding:'10px', borderRadius:'6px', border:'1px solid #cbd5e1', background: extType==='In'?'#dbeafe':'white', color: extType==='In'?'#1e40af':'#64748b', fontWeight:'bold'}}>In</button><button onClick={()=>setExtType('Out')} style={{flex:1, padding:'10px', borderRadius:'6px', border:'1px solid #cbd5e1', background: extType==='Out'?'#fee2e2':'white', color: extType==='Out'?'#991b1b':'#64748b', fontWeight:'bold'}}>Out</button></div></div></div>
              <div style={{ marginBottom: '15px' }}><label style={{ display:'block', fontSize:'13px', fontWeight:'bold', color:'#64748b', marginBottom:'5px' }}>{t('label_amount')}</label><input value={extAmount} onChange={e => setExtAmount(Number(e.target.value.replace(/[^0-9]/g,'')).toLocaleString())} style={{ width:'100%', padding:'12px', borderRadius:'6px', border:'1px solid #cbd5e1', fontSize:'16px', fontWeight:'bold' }} placeholder="0" /></div>
              <div style={{ marginBottom: '20px' }}><label style={{ display:'block', fontSize:'13px', fontWeight:'bold', color:'#64748b', marginBottom:'5px' }}>{t('label_remark')}</label><input value={extRemark} onChange={e => setExtRemark(e.target.value)} style={{ width:'100%', padding:'12px', borderRadius:'6px', border:'1px solid #cbd5e1' }} placeholder="..." /></div>
              <button onClick={handleExternalTx} style={{ width:'100%', padding:'15px', background:'#0f766e', color:'white', border:'none', borderRadius:'8px', fontWeight:'bold', cursor:'pointer' }}>{t('btn_submit')}</button>
            </div>
          )}

          {/* --- Tab Content: Handover --- */}
          {activeTab === 'Handover' && (
              <div>
                  <h3 style={{ marginTop:0, marginBottom:'20px', color:'#334155' }}>{t('tab_handover')}</h3>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'15px', marginBottom:'20px' }}>
                      <div>
                          <label style={{ display:'block', fontSize:'12px', fontWeight:'bold', color:'#64748b' }}>
                              Cash Chips
                              <span style={{ fontSize:'12px', color:'#2563eb', marginLeft:'8px' }}>
                                  (System: {fmt(systemState.cashChips)})
                              </span>
                          </label>
                          <input value={handoverData.cashChips} onChange={e => setHandoverData({...handoverData, cashChips: Number(e.target.value.replace(/[^0-9]/g,'')).toLocaleString()})} style={{ width:'100%', padding:'10px', border:'1px solid #cbd5e1', borderRadius:'6px' }} placeholder="0" />
                      </div>
                      <div>
                          <label style={{ display:'block', fontSize:'12px', fontWeight:'bold', color:'#d97706' }}>
                              Rolling Chips
                              <span style={{ fontSize:'12px', color:'#d97706', marginLeft:'8px' }}>
                                  (System: {fmt(systemState.rollingChips)})
                              </span>
                          </label>
                          <input value={handoverData.rollingChips} onChange={e => setHandoverData({...handoverData, rollingChips: Number(e.target.value.replace(/[^0-9]/g,'')).toLocaleString()})} style={{ width:'100%', padding:'10px', border:'1px solid #cbd5e1', borderRadius:'6px' }} placeholder="0" />
                      </div>
                      <div>
                          <label style={{ display:'block', fontSize:'12px', fontWeight:'bold', color:'#16a34a' }}>
                              Cash
                              <span style={{ fontSize:'12px', color:'#16a34a', marginLeft:'8px' }}>
                                  (System: {fmt(systemState.cash)})
                              </span>
                          </label>
                          <input value={handoverData.cash} onChange={e => setHandoverData({...handoverData, cash: Number(e.target.value.replace(/[^0-9]/g,'')).toLocaleString()})} style={{ width:'100%', padding:'10px', border:'1px solid #cbd5e1', borderRadius:'6px' }} placeholder="0" />
                      </div>
                  </div>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'15px', marginBottom:'20px' }}>
                      <div><label style={{ display:'block', fontSize:'12px', fontWeight:'bold', color:'#64748b' }}>Outgoing</label><input value={handoverData.outgoingStaff} onChange={e => setHandoverData({...handoverData, outgoingStaff: e.target.value})} style={{ width:'100%', padding:'10px', border:'1px solid #cbd5e1', borderRadius:'6px' }} placeholder="ID" />
                      {!handoverSigs.outgoing ? <button onClick={() => openSigModal('outgoing')} style={{ width:'100%', marginTop:'5px', padding:'8px', background:'#f8fafc', border:'1px solid #cbd5e1', borderRadius:'6px', fontSize:'12px' }}>Sign (Outgoing)</button> : <div style={{marginTop:'5px', color:'green', fontSize:'12px'}}>Signed</div>}</div>
                      <div><label style={{ display:'block', fontSize:'12px', fontWeight:'bold', color:'#64748b' }}>Incoming</label><input value={handoverData.incomingStaff} onChange={e => setHandoverData({...handoverData, incomingStaff: e.target.value})} style={{ width:'100%', padding:'10px', border:'1px solid #cbd5e1', borderRadius:'6px' }} placeholder="ID" />
                      {!handoverSigs.incoming ? <button onClick={() => openSigModal('incoming')} style={{ width:'100%', marginTop:'5px', padding:'8px', background:'#f8fafc', border:'1px solid #cbd5e1', borderRadius:'6px', fontSize:'12px' }}>Sign (Incoming)</button> : <div style={{marginTop:'5px', color:'green', fontSize:'12px'}}>Signed</div>}</div>
                  </div>
                  <button onClick={handleHandoverSubmit} style={{ width:'100%', padding:'15px', background:'#4f46e5', color:'white', border:'none', borderRadius:'8px', fontWeight:'bold', cursor:'pointer' }}>{t('save')}</button>
              </div>
          )}

          {/* --- Tab Content: Telegram --- */}
          {activeTab === 'Telegram' && (
            <div>
              <h3 style={{ marginTop:0, marginBottom:'20px', color:'#334155', display:'flex', alignItems:'center', gap:'10px' }}><Send size={24} color="#0ea5e9"/> {t('tab_telegram')}</h3>
              <div style={{ marginBottom: '15px' }}><label style={{ display:'block', fontSize:'13px', fontWeight:'bold', color:'#64748b', marginBottom:'5px' }}>Recipients</label><div style={{ display:'flex', gap:'10px', marginBottom:'10px' }}><div style={{ flex:1, position:'relative' }}><input placeholder="Search Agent..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} style={{ width:'100%', padding:'12px', borderRadius:'6px', border:'1px solid #cbd5e1' }} />{searchTerm && (<div style={{ position:'absolute', top:'100%', left:0, width:'100%', maxHeight:'200px', overflowY:'auto', background:'white', border:'1px solid #cbd5e1', zIndex:10 }}>{filteredAgents.map(a => (<div key={a.id} onClick={() => handleAddRecipient(a)} style={{ padding:'10px', cursor:'pointer', borderBottom:'1px solid #f1f5f9' }}><span style={{ fontWeight:'bold', color:'#2563eb' }}>{getAgentDisplayName(a)}</span></div>))}</div>)}</div><button onClick={handleSelectAllAgents} style={{ padding:'0 15px', background:'#e2e8f0', border:'none', borderRadius:'6px', fontWeight:'bold' }}>Select All</button></div>
              <div style={{ minHeight:'40px', padding:'10px', border:'1px solid #e2e8f0', borderRadius:'6px', background:'#f8fafc', display:'flex', flexWrap:'wrap', gap:'5px' }}>{msgRecipients.map(agent => (<div key={agent.id} style={{ display:'flex', alignItems:'center', gap:'5px', padding:'5px 10px', background:'white', border:'1px solid #cbd5e1', borderRadius:'15px', fontSize:'12px' }}><strong style={{ color:'#2563eb' }}>{agent.code}</strong><X size={14} style={{ cursor:'pointer' }} onClick={() => handleRemoveRecipient(agent.id)} /></div>))}</div></div>
              <div style={{ marginBottom: '20px' }}><label style={{ display:'block', fontSize:'13px', fontWeight:'bold', color:'#64748b', marginBottom:'5px' }}>Message Content</label><textarea value={msgContent} onChange={e => setMsgContent(e.target.value)} style={{ width:'100%', padding:'12px', borderRadius:'6px', border:'1px solid #cbd5e1', height:'120px', resize:'none' }} placeholder="..." /><div style={{ marginTop:'10px', display:'flex', gap:'5px', flexWrap:'wrap' }}>{smsTemplates.map(tmpl => (<div key={tmpl.id} style={{ display:'flex', alignItems:'center', gap:'3px', fontSize:'11px', padding:'5px 10px', background:'#f1f5f9', border:'1px solid #e2e8f0', borderRadius:'15px' }}><span style={{ cursor:'pointer' }} onClick={() => setMsgContent(tmpl.content)}>#{tmpl.title}</span><X size={12} style={{ cursor:'pointer', marginLeft:'3px', color:'#ef4444' }} onClick={() => deleteSmsTemplate(tmpl.id)} /></div>))}<button onClick={handleSaveTemplate} style={{ display:'flex', alignItems:'center', gap:'5px', padding:'6px 12px', background:'#10b981', color:'white', border:'none', borderRadius:'6px', fontSize:'12px', fontWeight:'bold', cursor:'pointer' }}>Save Template</button></div></div>
              <button onClick={handleTelegramSend} style={{ width:'100%', padding:'15px', background:'#0ea5e9', color:'white', border:'none', borderRadius:'8px', fontWeight:'bold', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:'8px' }}><Send size={20}/> {t('action')}</button>
            </div>
          )}
        </div>

        {/* --- Right Panel: Logs (모든 탭에서 표시) --- */}
        <div style={{ width:'350px', backgroundColor:'white', padding:'20px', borderRadius:'12px', boxShadow:'0 4px 15px rgba(0,0,0,0.05)', display:'flex', flexDirection:'column' }}>
            <h4 style={{ margin:'0 0 15px 0', color:'#64748b' }}>Recent Logs ({activeTab === 'External' ? 'Vault & Bank' : activeTab})</h4>
            <div style={{ flex:1, overflowY:'auto' }}>
                {activeTab === 'Agent' ? (
                    agentTransactions.slice(0, 10).map((tx, i) => (
                        <div key={i} style={{ padding:'10px', borderBottom:'1px solid #f1f5f9', fontSize:'13px' }}>
                            <div style={{ display:'flex', justifyContent:'space-between' }}>
                                <span style={{ fontWeight:'bold', color:'#0f172a' }}>
                                    {findAgentByCode(tx.agentCode) ? getAgentDisplayName(findAgentByCode(tx.agentCode)) : tx.agentCode}
                                </span>
                                <span style={{ color:'#64748b' }}>{new Date(tx.date).toLocaleTimeString()}</span>
                            </div>
                            <div style={{ display:'flex', justifyContent:'space-between', marginTop:'4px' }}><span style={{ color: tx.type==='Deposit'?'#16a34a':'#dc2626' }}>{tx.type}</span><span style={{ fontWeight:'bold' }}>{fmt(tx.amount)}</span></div>
                        </div>
                    ))
                ) : activeTab === 'Telegram' ? (
                    telegramLogs.slice(0, 10).map((log, i) => (
                        <div key={i} style={{ padding:'10px', borderBottom:'1px solid #f1f5f9', fontSize:'12px' }}>
                            <div style={{ display:'flex', justifyContent:'space-between' }}>
                                <span style={{ fontWeight:'bold', color:'#0f172a' }}>
                                    To: {findAgentByCode(log.to) ? getAgentDisplayName(findAgentByCode(log.to)) : log.to}
                                </span>
                                <span style={{ color:'#64748b' }}>{new Date(log.date).toLocaleTimeString()}</span>
                            </div>
                            <div style={{ marginTop:'4px', color:'#334155' }}>{log.message}</div>
                            <div style={{ textAlign:'right', fontSize:'10px', color: log.status==='Sent'?'green':'orange' }}>{log.status}</div>
                        </div>
                    ))
                ) : activeTab === 'Handover' ? (
                    handoverLogs.slice(0, 10).map((log, i) => (
                        <div key={i} style={{ padding:'10px', borderBottom:'1px solid #f1f5f9', fontSize:'12px' }}>
                            <div style={{ color:'#64748b', marginBottom:'2px' }}>{new Date(log.date).toLocaleString()}</div>
                            <div style={{ fontWeight:'bold', color:'#0f172a' }}>{log.outgoingStaff} → {log.incomingStaff}</div>
                            <div style={{ marginTop:'4px' }}>
                                <div style={{ display:'flex', justifyContent:'space-between' }}><div><span style={{color:'#64748b'}}>CashChips: {fmt(log.cashChips)}</span><div style={{ fontSize:'11px', color:'#d97706', marginTop:'1px' }}>Rolling Chips: {fmt(log.rollingChips)}</div></div><span style={{color:'#64748b', fontWeight:'bold'}}>Cash: {fmt(log.cash)}</span></div>
                            </div>
                        </div>
                    ))
                ) : (
                    cageLogs.filter(l => {
                        if (activeTab === 'Safety') return l.type === 'Safety Deposit';
                        if (activeTab === 'Expense') return l.type === 'Expense';
                        if (activeTab === 'External') return l.type === 'External';
                        return false;
                    }).slice(0, 10).map((log, i) => (
                        <div key={i} style={{ padding:'10px', borderBottom:'1px solid #f1f5f9', fontSize:'13px' }}>
                            <div style={{ display:'flex', justifyContent:'space-between' }}>
                                <span style={{ fontWeight:'bold', color:'#0f172a' }}>{log.target ? `${log.target} (${log.itemType})` : (log.category || log.depositor)}</span>
                                <span style={{ color:'#64748b' }}>{new Date(log.date).toLocaleTimeString()}</span>
                            </div>
                            <div style={{ display:'flex', justifyContent:'space-between', marginTop:'4px' }}>
                                <span style={{ color: '#64748b' }}>{activeTab === 'External' ? log.category : log.type}</span>
                                <span style={{ fontWeight:'bold', color: (log.type==='Expense' || log.category==='Out') ?'#dc2626':'#2563eb' }}>{fmt(log.amount)}</span>
                            </div>
                            {log.remark && <div style={{ color:'#94a3b8', fontSize:'11px' }}>{log.remark}</div>}
                        </div>
                    ))
                )}
            </div>
        </div>
      </div>
      
      {/* --- Modals (Signature, Success, Print) --- */}

      {/* Signature Modal with Bluetooth Button */}
      {isSigModalOpen && (
          <div style={{ position:'fixed', top:0, left:0, width:'100%', height:'100%', background:'rgba(0,0,0,0.5)', display:'flex', justifyContent:'center', alignItems:'center', zIndex:2000 }}>
              <div style={{ background:'white', padding:'20px', borderRadius:'12px', width:'500px' }}>
                  <h3 style={{ marginTop:0 }}>{t('lbl_sig')}</h3>
                  <canvas ref={canvasRef} width={460} height={200} style={{ border:'1px solid #000', cursor:'crosshair', background:'#fff' }} onMouseDown={startDrawing} onMouseMove={draw} onMouseUp={() => setIsDrawing(false)} onMouseLeave={() => setIsDrawing(false)} onTouchStart={startDrawing} onTouchMove={draw} onTouchEnd={() => setIsDrawing(false)} />
                  <div style={{ marginTop:'15px', display:'flex', gap:'10px' }}>
                      {/* 블루투스 연결 버튼 */}
                      <button onClick={connectBluetoothPad} style={{ padding:'10px', background:'#0ea5e9', color:'white', border:'none', borderRadius:'6px', display:'flex', alignItems:'center', gap:'5px', cursor:'pointer' }}><Bluetooth size={16}/> {t('btn_connect')}</button>
                      <button onClick={() => { const ctx=canvasRef.current.getContext('2d'); ctx.clearRect(0,0,460,200); }} style={{ padding:'10px', background:'#f1f5f9', border:'1px solid #cbd5e1', borderRadius:'6px', cursor:'pointer' }}>Clear</button>
                      <button onClick={saveSignature} style={{ flex:1, padding:'10px', background:'#2563eb', color:'white', border:'none', borderRadius:'6px', fontWeight:'bold', cursor:'pointer' }}>{t('save')}</button>
                      <button onClick={()=>setIsSigModalOpen(false)} style={{ padding:'10px', background:'white', border:'1px solid #ccc', borderRadius:'6px', cursor:'pointer' }}>{t('cancel')}</button>
                  </div>
              </div>
          </div>
      )}

      {/* Success & Print Modal */}
      {showSuccessModal && (
          <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.85)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 2000 }}>
              <div style={{ backgroundColor: 'white', width: '500px', padding: '40px', borderRadius: '25px', textAlign: 'center', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)' }}>
                  <div style={{ display:'flex', justifyContent:'center', marginBottom:'20px' }}><CheckCircle size={70} color="#16a34a" /></div>
                  <h2 style={{ fontSize: '24px', margin: '0 0 10px 0', color: '#1e293b' }}>Success!</h2>
                  <p style={{ color: '#64748b', fontSize: '16px', marginBottom: '30px' }}>Record saved.<br/>Print receipt?</p>
                  <div style={{ display: 'flex', gap: '10px' }}>
                      <button onClick={() => { window.print(); setShowSuccessModal(false); }} style={{ flex:1, padding: '15px', backgroundColor: '#2563eb', color: 'white', border: 'none', borderRadius: '12px', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:'8px' }}><Printer size={20}/> {t('pdf')}</button>
                      <button onClick={() => setShowSuccessModal(false)} style={{ width: '100px', padding: '15px', backgroundColor: '#e2e8f0', color: '#475569', border: 'none', borderRadius: '12px', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer' }}>{t('close')}</button>
                  </div>
              </div>
          </div>
      )}

      {/* ★ 실제 로고 및 프린터 설정 적용된 영수증 */}
      {lastSafetyLog && (
          <div id="receipt-print" style={{ display:'none' }}>
              <div style={{ width:'100%', height:'100%', padding: sysConfig?.printerSettings?.slip?.margin || '5mm', boxSizing:'border-box', border:'3px double #000', margin:'0 auto', fontFamily:'Arial, sans-serif', position:'relative', display:'flex', flexDirection:'column' }}>
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'20px' }}>
                      <div style={{ width:'200px', display:'flex', justifyContent:'flex-start' }}>
                          {sysConfig?.logoData ? 
                              <img src={sysConfig.logoData} alt="Logo" style={{ maxHeight:'60px', maxWidth:'150px' }} /> :
                              <div style={{ width:'100px', height:'60px', border:'1px dashed #ccc', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'12px', color:'#999' }}>LOGO</div>
                          }
                      </div>
                      <div style={{ flex:1, textAlign:'center' }}><h1 style={{ margin:0, fontSize:'24px', fontWeight:'900' }}>DEPOSIT SLIP</h1></div>
                      <div style={{ width:'200px', textAlign:'right', fontSize:'12px' }}><div>{lastSafetyLog.id}</div><div>{new Date(lastSafetyLog.date).toLocaleString()}</div></div>
                  </div>
                  
                  <div style={{ flex:1 }}>
                      <div style={{ fontSize:'16px', marginBottom:'10px' }}><strong>Depositor:</strong> {lastSafetyLog.depositor}</div>
                      <div style={{ fontSize:'16px', marginBottom:'10px' }}><strong>Amount:</strong> {lastSafetyLog.currency} {fmt(lastSafetyLog.amount)}</div>
                      <div style={{ fontSize:'14px', color:'#555' }}>Remark: {lastSafetyLog.remark}</div>
                      
                      {/* 서명 표시 */}
                      {lastSafetyLog.signature && (
                          <div style={{ marginTop:'20px' }}>
                              <div style={{ fontSize:'12px', fontWeight:'bold', marginBottom:'5px' }}>Depositor Signature:</div>
                              <img src={lastSafetyLog.signature} alt="Signature" style={{ maxHeight:'50px', borderBottom:'1px solid #000' }} />
                          </div>
                      )}
                  </div>
                  <div style={{ marginTop:'auto', textAlign:'center', fontSize:'12px' }}>{sysConfig?.slipTemplate?.footerText}</div>
              </div>
          </div>
      )}

      {/* ★ 동적 프린터 스타일 (용지 크기 반영) */}
      <style>{`
        @media print {
            @page { 
                size: ${sysConfig?.printerSettings?.slip?.paperSize || 'A5'} ${sysConfig?.printerSettings?.slip?.orientation || 'landscape'}; 
                margin: 0; 
            }
            html, body { height: 100%; overflow: hidden !important; }
            body * { visibility: hidden; }
            #receipt-print, #receipt-print * { visibility: visible; display: flex !important; }
            #receipt-print { position: absolute; left: 0; top: 0; width: 100vw; height: 100vh; background: white; }
            .no-print { display: none !important; }
        }
      `}</style>
    </div>
  );
};

export default Cage;